// ReservedTools — the top-bar "app tools" placeholders: search (⌘K) and
// notifications. RESERVED seams (APP-139 search / APP-065 notifications)
// rendered now as placeholders so the shell reads as a real product; activating
// either surfaces a "coming soon" toast. Swap the toast for the real surface
// when each feature lands (no layout change needed).
//
// Exported as separate pieces because they sit on opposite sides of the project
// switcher: search leads the right zone; notifications clusters next to the gear
// on the far right.
//
//   Search          — soft-grey pill on xl+ (looks real on wide desktop),
//                     collapses to an icon below xl so the domains keep room.
//   Notifications   — always visible (glanceable); placeholder unread dot.
//
// Help (APP-140) is intentionally NOT a shell placeholder: the earlier lg+-only
// `?` created responsive drift (present on desktop, gone on tablet/phone). It
// returns coherently across every breakpoint when the help layer is designed.

import { useTranslation } from 'react-i18next';
import { icons } from '../ui/icons.js';
import { IconButton } from '../ui/IconButton.js';
import { useToast } from '../ui/Toast.js';

function useComingSoon() {
  const { t } = useTranslation('nav');
  const toast = useToast();
  return () => toast.info(t('tools.comingSoon'));
}

export function SearchPlaceholder() {
  const { t } = useTranslation('nav');
  const soon = useComingSoon();
  return (
    <>
      {/* Search-field-shaped pill on xl+ (standard pill→icon collapse, like
          GitHub's): advertises searchability; the ⌘K hint educates ahead of the
          command-palette search. It IS a search field, so it wears the shared
          `field-frame` recipe (border + hover + any-modality focus light-up) —
          not a bespoke fill that vanishes on a white chrome. Icon below xl.
          A slim toolbar control, so it opts out of field-frame's 2.5rem form-field
          height (`min-h-0` + `h-9` = 36px) — denser than a form field by design. */}
      <button
        type="button"
        onClick={soon}
        aria-label={t('tools.search')}
        className="hidden xl:flex items-center gap-2 min-h-0 h-9 w-56 px-3 rounded-md field-frame cursor-pointer
          bg-[var(--color-bg-page)] text-sm text-[var(--color-text-secondary)]
          transition-colors outline-none"
      >
        <icons.search aria-hidden size={16} className="shrink-0" />
        <span className="flex-1 text-start truncate">{t('tools.searchPlaceholder')}</span>
        <span aria-hidden className="shrink-0 text-xs text-[var(--color-text-disabled)]">
          {t('tools.searchShortcut')}
        </span>
      </button>
      <IconButton className="xl:hidden" aria-label={t('tools.search')} onClick={soon}>
        <icons.search aria-hidden size={20} />
      </IconButton>
    </>
  );
}

export function NotificationsButton() {
  const { t } = useTranslation('nav');
  const soon = useComingSoon();
  return (
    <div className="relative inline-flex">
      <IconButton aria-label={t('tools.notifications')} onClick={soon}>
        <icons.notifications aria-hidden size={20} />
      </IconButton>
      <span
        aria-hidden
        className="absolute top-1.5 end-1.5 w-2 h-2 rounded-full bg-[var(--color-primary-display)] ring-2 ring-[var(--color-bg-page)]"
      />
    </div>
  );
}

